import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import jsPDF from "jspdf";
import "jspdf-autotable";

import { useGetOtherExpensesQuery } from "../../slices/otherExpensesApiSlice";

export default function Balance() {
  const today = new Date().toISOString().split("T")[0];
  const [date1, setDate1] = useState(today);
  const [date2, setDate2] = useState(today);
  const [mode, setMode] = useState("daily"); // 'daily' | 'monthly'
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [userFilter, setUserFilter] = useState("All");

  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({
    totalSales: 0,
    totalExpenses: 0,
    totalOtherExpense: 0,
    totalIncomes: 0,
    totalDeposit: 0,
    totalPurchases: 0,
    totalCashInHand: 0,
    totalNetCash: 0,
    totalDifference: 0,
  });

  const { data: salesData, isLoading: salesLoading } = useGetSalesQuery();
  const { data: expensesData, isLoading: expensesLoading } = useGetExpensesQuery({
    startDate: date1,
    endDate: date2,
  });
  const { data: incomesData, isLoading: incomesLoading } = useGetIncomesQuery({
    startDate: date1,
    endDate: date2,
  });
  const { data: depositsData, isLoading: depositsLoading } = useGetDepositsQuery({
    startDate: date1,
    endDate: date2,
  });
  const { data: suppliesData, isLoading: suppliesLoading } = useGetSuppliesQuery({
    startDate: date1,
    endDate: date2,
  });
  const { data: otherExpensesData, isLoading: otherExpensesLoading } =
    useGetOtherExpensesQuery({
      startDate: date1,
      endDate: date2,
    });

  const loading =
    salesLoading ||
    expensesLoading ||
    incomesLoading ||
    depositsLoading ||
    suppliesLoading ||
    otherExpensesLoading;

  const getKey = (date) => {
    const d = new Date(date);
    if (mode === "monthly") {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
    return d.toISOString().split("T")[0];
  };

  const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
    const end = new Date(endDate);

    while (currentDate <= end) {
      dates.push(currentDate.toISOString().split("T")[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  const usersList = useMemo(() => {
    const users = new Set();
    [
      salesData?.sales,
      expensesData?.expenses,
      incomesData?.incomes,
      depositsData?.deposits,
      suppliesData?.supplies,
      otherExpensesData?.otherExpenses,
    ].forEach((arr) => {
      arr?.forEach((item) => {
        if (item.username) users.add(item.username);
      });
    });
    return ["All", ...Array.from(users)];
  }, [
    salesData,
    expensesData,
    incomesData,
    depositsData,
    suppliesData,
    otherExpensesData,
  ]);

  const categoriesList = useMemo(() => {
    const cats = new Set();
    otherExpensesData?.otherExpenses?.forEach((exp) => {
      if (exp.category) cats.add(exp.category);
    });
    expensesData?.expenses?.forEach((exp) => {
      if (exp.category) cats.add(exp.category);
    });
    return ["All", ...Array.from(cats)];
  }, [otherExpensesData, expensesData]);

  const filterByUser = (item) =>
    userFilter === "All" ? true : item.username === userFilter;

  const filterByCategory = (item) =>
    categoryFilter === "All" ? true : item.category === categoryFilter;

  const calculateReport = () => {
    const dates = getDatesBetween(date1, date2);
    const keys = mode === "daily" ? dates : Array.from(
      new Set(dates.map((d) => getKey(d)))
    );

    const result = keys.map((key) => {
      const sales = salesData?.sales
        ?.filter(
          (sale) =>
            sale.date &&
            getKey(sale.date) === key &&
            filterByUser(sale)
        )
        .reduce((sum, sale) => sum + Number(sale.totalPrice || 0), 0) || 0;

      const expenses = expensesData?.expenses
        ?.filter(
          (expense) =>
            expense.date &&
            getKey(expense.date) === key &&
            filterByUser(expense) &&
            filterByCategory(expense)
        )
        .reduce((sum, expense) => sum + Number(expense.amount || 0), 0) || 0;

      const otherEx = otherExpensesData?.otherExpenses
        ?.filter(
          (exp) =>
            exp.date &&
            getKey(exp.date) === key &&
            filterByUser(exp) &&
            filterByCategory(exp)
        )
        .reduce((sum, exp) => sum + Number(exp.amount || 0), 0) || 0;

      const incomes = incomesData?.incomes
        ?.filter(
          (income) =>
            income.date &&
            getKey(income.date) === key &&
            filterByUser(income)
        )
        .reduce((sum, income) => sum + Number(income.amount || 0), 0) || 0;

      const deposits = depositsData?.deposits
        ?.filter(
          (deposit) =>
            deposit.date &&
            getKey(deposit.date) === key &&
            filterByUser(deposit)
        )
        .reduce((sum, deposit) => sum + Number(deposit.amount || 0), 0) || 0;

      const netBank = deposits > 0 ? deposits * -1 : deposits * +1;

      const purchases = suppliesData?.supplies
        ?.filter(
          (supply) =>
            supply.date &&
            getKey(supply.date) === key &&
            filterByUser(supply)
        )
        .reduce((sum, supply) => sum + Number(supply.cardCost || 0), 0) * -1 || 0;

      const netCash = sales + expenses;
      const difference = incomes - netCash;
      const cashInHand = netCash + netBank + otherEx + purchases;

      return {
        key,
        netSales: sales,
        netExpenses: expenses,
        netOtherExpense: otherEx,
        netCash,
        cashCollected: incomes,
        difference,
        netBank,
        purchases,
        cashInHand,
      };
    });

    setReportData(result);
    calculateTotals(result);
  };

  const calculateTotals = (data) => {
    const totalSales = data.reduce((sum, row) => sum + (row.netSales || 0), 0);
    const totalExpenses = data.reduce(
      (sum, row) => sum + (row.netExpenses || 0),
      0
    );
    const totalOtherExpense = data.reduce(
      (sum, row) => sum + (row.netOtherExpense || 0),
      0
    );
    const totalIncomes = data.reduce(
      (sum, row) => sum + (row.cashCollected || 0),
      0
    );
    const totalDeposit = data.reduce(
      (sum, row) => sum + (row.netBank || 0),
      0
    );
    const totalPurchases = data.reduce(
      (sum, row) => sum + (row.purchases || 0),
      0
    );
    const totalCashInHand = data.reduce(
      (sum, row) => sum + (row.cashInHand || 0),
      0
    );
    const totalNetCash = totalSales + totalExpenses + totalOtherExpense;
    const totalDifference = totalIncomes - totalNetCash;

    setTotals({
      totalSales: totalSales.toFixed(2),
      totalExpenses: totalExpenses.toFixed(2),
      totalOtherExpense: totalOtherExpense.toFixed(2),
      totalIncomes: totalIncomes.toFixed(2),
      totalDeposit: totalDeposit.toFixed(2),
      totalPurchases: totalPurchases.toFixed(2),
      totalCashInHand: totalCashInHand.toFixed(2),
      totalNetCash: totalNetCash.toFixed(2),
      totalDifference: totalDifference.toFixed(2),
    });
  };

  const exportToExcel = () => {
    const exportData = reportData.map((row) => ({
      Period: row.key,
      NetSales: row.netSales.toFixed(2),
      NetExpenses: row.netExpenses.toFixed(2),
      OtherEx: row.netOtherExpense.toFixed(2),
      NetCash: row.netCash.toFixed(2),
      CashCollected: row.cashCollected.toFixed(2),
      Difference: row.difference.toFixed(2),
      NetBank: row.netBank.toFixed(2),
      Purchases: row.purchases.toFixed(2),
      CashInHand: row.cashInHand.toFixed(2),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    // Auto-width
    const colWidths = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = worksheet[cellAddress];
        if (cell && cell.v) {
          const len = String(cell.v).length;
          if (len > maxWidth) maxWidth = len;
        }
      }
      colWidths.push({ wch: maxWidth + 2 });
    }
    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Report");
    XLSX.writeFile(workbook, "BalanceReport.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const columns = [
      "Period",
      "NetSales",
      "NetExpenses",
      "OtherEx",
      "NetCash",
      "CashCollected",
      "Difference",
      "NetBank",
      "Purchases",
      "CashInHand",
    ];
    const rows = reportData.map((row) => [
      row.key,
      row.netSales.toFixed(2),
      row.netExpenses.toFixed(2),
      row.netOtherExpense.toFixed(2),
      row.netCash.toFixed(2),
      row.cashCollected.toFixed(2),
      row.difference.toFixed(2),
      row.netBank.toFixed(2),
      row.purchases.toFixed(2),
      row.cashInHand.toFixed(2),
    ]);

    doc.text("Balance Report", 14, 16);
    doc.autoTable({
      head: [columns],
      body: rows,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [40, 40, 40], textColor: 255 },
    });
    doc.save("BalanceReport.pdf");
  };

  return (
    <div className="balance-report">
      <h1>Balance Report</h1>

      <div className="controls">
        <div className="date-range">
          <label>
            Start Date:
            <input
              type="date"
              value={date1}
              onChange={(e) => setDate1(e.target.value)}
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              value={date2}
              onChange={(e) => setDate2(e.target.value)}
            />
          </label>
        </div>

        <div className="mode-toggle">
          <label>
            View:
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
        </div>

        <div className="filters">
          <label>
            User:
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              {usersList.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>

          <label>
            Category:
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {categoriesList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <button onClick={calculateReport} className="calculate-button">
        Calculate Report
      </button>

      {loading ? (
        <div>Loading data...</div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>{mode === "daily" ? "Date" : "Month"}</th>
                <th>Net Sales</th>
                <th>Net Expenses</th>
                
                <th>Net Cash</th>
                <th>Cash Collected</th>
                <th>Difference</th>
                <th>Net Bank</th>
                <th>OtherEx</th>
                <th>Purchases</th>
                <th>Cash In Hand</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row) => (
                <tr key={row.key}>
                  <td>{row.key}</td>
                  <td>{row.netSales.toFixed(2)}</td>
                  <td>{row.netExpenses.toFixed(2)}</td>
                  
                  <td>{row.netCash.toFixed(2)}</td>
                  <td>{row.cashCollected.toFixed(2)}</td>
                  <td>{row.difference.toFixed(2)}</td>
                  <td>{row.netBank.toFixed(2)}</td>
                  <td>{row.netOtherExpense.toFixed(2)}</td>
                  <td>{row.purchases.toFixed(2)}</td>
                  <td>{row.cashInHand.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td><strong>{totals.totalSales}</strong></td>
                <td><strong>{totals.totalExpenses}</strong></td>
                <td><strong>{totals.totalOtherExpense}</strong></td>
                <td><strong>{totals.totalNetCash}</strong></td>
                <td><strong>{totals.totalIncomes}</strong></td>
                <td><strong>{totals.totalDifference}</strong></td>
                <td><strong>{totals.totalDeposit}</strong></td>
                <td><strong>{totals.totalPurchases}</strong></td>
                <td><strong>{totals.totalCashInHand}</strong></td>
              </tr>
            </tfoot>
          </table>

          <div className="export-buttons">
            <button onClick={exportToExcel} className="export-button">
              Export to Excel
            </button>
            <button onClick={exportToPDF} className="export-button">
              Export to PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
}
